// Lightweight pinch-to-zoom for CoolLightBox images on mobile
// This augments default behavior by scaling the current slide image when pinched.
// Also enables panning (image movement) when zoomed in.

let teardown = null;

function getScaleFromElement(element) {
  const value = parseFloat(element?.dataset?.pinchScale || '1');
  return Number.isFinite(value) ? value : 1;
}

function getTranslateFromElement(element) {
  const x = parseFloat(element?.dataset?.pinchTranslateX || '0');
  const y = parseFloat(element?.dataset?.pinchTranslateY || '0');
  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0
  };
}

function setTranslateOnElement(element, translateX, translateY) {
  element.dataset.pinchTranslateX = String(translateX);
  element.dataset.pinchTranslateY = String(translateY);
}

function resetTranslate(element) {
  element.dataset.pinchTranslateX = '0';
  element.dataset.pinchTranslateY = '0';
}

function getImageBounds(img, container) {
  const scale = getScaleFromElement(img);
  
  // Get container dimensions
  const containerRect = container ? container.getBoundingClientRect() : {
    width: window.innerWidth,
    height: window.innerHeight
  };
  
  // Get base image dimensions (before transform)
  // Store base dimensions in dataset on first use for consistent calculations
  let imgWidth = 0;
  let imgHeight = 0;
  
  if (img.dataset.baseWidth && img.dataset.baseHeight) {
    // Use cached base dimensions
    imgWidth = parseFloat(img.dataset.baseWidth);
    imgHeight = parseFloat(img.dataset.baseHeight);
  } else {
    // Calculate and cache base dimensions
    // Try natural dimensions first (most accurate)
    if (img.naturalWidth && img.naturalHeight && img.naturalWidth > 0 && img.naturalHeight > 0) {
      imgWidth = img.naturalWidth;
      imgHeight = img.naturalHeight;
    } else {
      // Get current rendered size and divide by current scale to get base size
      const currentScale = scale > 1 ? scale : 1;
      const imgRect = img.getBoundingClientRect();
      imgWidth = (imgRect.width / currentScale) || img.offsetWidth || 0;
      imgHeight = (imgRect.height / currentScale) || img.offsetHeight || 0;
      
      // Fallback to computed style
      if (!imgWidth || !imgHeight) {
        const computedStyle = window.getComputedStyle(img);
        imgWidth = parseFloat(computedStyle.width) || 0;
        imgHeight = parseFloat(computedStyle.height) || 0;
      }
    }
    
    // Cache the base dimensions
    if (imgWidth > 0 && imgHeight > 0) {
      img.dataset.baseWidth = String(imgWidth);
      img.dataset.baseHeight = String(imgHeight);
    }
  }
  
  // If still no valid dimensions, use container size as fallback
  if (!imgWidth || !imgHeight || imgWidth === 0 || imgHeight === 0) {
    imgWidth = containerRect.width;
    imgHeight = containerRect.height;
  }
  
  // Calculate scaled dimensions
  const imgScaledWidth = imgWidth * scale;
  const imgScaledHeight = imgHeight * scale;
  
  // Calculate how much the scaled image extends beyond the container
  // The image is centered, so we can move it half of the overflow in each direction
  const overflowX = Math.max(0, (imgScaledWidth - containerRect.width) / 2);
  const overflowY = Math.max(0, (imgScaledHeight - containerRect.height) / 2);
  
  return {
    minX: -overflowX,
    maxX: overflowX,
    minY: -overflowY,
    maxY: overflowY,
    scale: scale
  };
}

function clampTranslate(translateX, translateY, bounds) {
  return {
    x: Math.max(bounds.minX, Math.min(bounds.maxX, translateX)),
    y: Math.max(bounds.minY, Math.min(bounds.maxY, translateY))
  };
}

function setScaleOnElement(element, scale, translateX = null, translateY = null) {
  const clampedScale = Math.max(1, Math.min(scale, 3));
  element.dataset.pinchScale = String(clampedScale);

  // Get current translate if not provided
  if (translateX === null || translateY === null) {
    const currentTranslate = getTranslateFromElement(element);
    translateX = currentTranslate.x;
    translateY = currentTranslate.y;
  }

  // Get container to calculate bounds
  const slide = element.closest('.cool-lightbox__slide');
  const container = slide || element.parentElement;
  const bounds = getImageBounds(element, container);
  
  // Clamp translate to bounds
  const clamped = clampTranslate(translateX, translateY, bounds);
  setTranslateOnElement(element, clamped.x, clamped.y);

  // Apply transform with both scale and translate
  element.style.transformOrigin = 'center center';
  element.style.willChange = 'transform';
  element.style.touchAction = 'none';
  
  if (clampedScale > 1) {
    element.style.transform = `translate(${clamped.x}px, ${clamped.y}px) scale(${clampedScale})`;
  } else {
    element.style.transform = `scale(${clampedScale})`;
  }
}

function resetScale(element) {
  element.dataset.pinchScale = '1';
  resetTranslate(element);
  element.style.transform = '';
  element.style.willChange = '';
  // Clear cached base dimensions when resetting (in case image changes)
  delete element.dataset.baseWidth;
  delete element.dataset.baseHeight;
}

function onMounted() {
  let startDistance = 0;
  let startScale = 1;
  let isPinching = false;
  let isPanning = false;
  let activeImg = null;
  let swipeBlockedUntil = 0; // timestamp when swipe events should be unblocked
  let panStartX = 0;
  let panStartY = 0;
  let panStartTranslateX = 0;
  let panStartTranslateY = 0;
  const optsStart = { passive: false, capture: true };
  const optsMove = { passive: false, capture: true };
  const optsEnd = { passive: true, capture: true };

  function getActiveImage(target) {
    // Find current slide image within the lightbox
    // Targets can be inside .cool-lightbox__slide__img
    const slide = target.closest('.cool-lightbox__slide');
    if (!slide) return null;
    
    const img = slide.querySelector('.cool-lightbox__slide__img img, .cool-lightbox__image img, .cool-lightbox__slide img');
    
    // If we have an activeImg from a different slide, reset the previous one
    if (activeImg && img && activeImg !== img) {
      const prevScale = getScaleFromElement(activeImg);
      if (prevScale > 1.02) {
        resetScale(activeImg);
      }
      // Reset state when switching to a different image
      isPanning = false;
      isPinching = false;
    }
    
    return img || null;
  }
  
  function isValidActiveImage(img) {
    if (!img) return false;
    // Check if image is still in DOM
    if (!img.isConnected) return false;
    // Check if it's in a visible slide
    const slide = img.closest('.cool-lightbox__slide');
    if (!slide) return false;
    // Check if slide is visible (basic check)
    const style = window.getComputedStyle(slide);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function distance(t1, t2) {
    const dx = t2.pageX - t1.pageX;
    const dy = t2.pageY - t1.pageY;
    return Math.hypot(dx, dy);
  }

  function handleTouchStart(e) {
    const img = getActiveImage(e.target);
    const currentScale = img ? getScaleFromElement(img) : 1;

    // Handle single touch when zoomed in (panning)
    if (e.touches.length === 1 && img && currentScale > 1.02) {
      // Start panning - block lightbox swipe when panning zoomed image
      isPanning = true;
      isPinching = false;
      activeImg = img;
      panStartX = e.touches[0].pageX;
      panStartY = e.touches[0].pageY;
      const currentTranslate = getTranslateFromElement(img);
      panStartTranslateX = currentTranslate.x;
      panStartTranslateY = currentTranslate.y;
      
      // Block lightbox swipe when panning zoomed image
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
      return;
    }

    // Block swipe events if we're still in the cooldown period after pinch (and not zoomed)
    if (e.touches.length === 1 && Date.now() < swipeBlockedUntil && (!img || currentScale <= 1.02)) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
      return;
    }

    if (e.touches.length === 2) {
      activeImg = getActiveImage(e.target);
      if (!activeImg) return;
      isPinching = true;
      isPanning = false;
      startDistance = distance(e.touches[0], e.touches[1]);
      startScale = getScaleFromElement(activeImg);
      // Block lightbox swipe logic during pinch
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
    }
  }

  function handleTouchMove(e) {
    // Validate active image
    if (activeImg && !isValidActiveImage(activeImg)) {
      // Image is no longer valid, reset state
      const prevScale = getScaleFromElement(activeImg);
      if (prevScale > 1.02) {
        resetScale(activeImg);
      }
      activeImg = null;
      isPanning = false;
      isPinching = false;
      return;
    }

    // If we were pinching and now have only one touch, switch to panning if zoomed
    if (isPinching && e.touches.length === 1 && activeImg && isValidActiveImage(activeImg)) {
      const currentScale = getScaleFromElement(activeImg);
      if (currentScale > 1.02) {
        // Switch from pinch to pan
        isPinching = false;
        isPanning = true;
        panStartX = e.touches[0].pageX;
        panStartY = e.touches[0].pageY;
        const currentTranslate = getTranslateFromElement(activeImg);
        panStartTranslateX = currentTranslate.x;
        panStartTranslateY = currentTranslate.y;
      }
    }

    // Handle panning (single touch when zoomed)
    if (isPanning && activeImg && isValidActiveImage(activeImg) && e.touches.length === 1) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();

      const deltaX = e.touches[0].pageX - panStartX;
      const deltaY = e.touches[0].pageY - panStartY;
      const newTranslateX = panStartTranslateX + deltaX;
      const newTranslateY = panStartTranslateY + deltaY;
      
      const currentScale = getScaleFromElement(activeImg);
      setScaleOnElement(activeImg, currentScale, newTranslateX, newTranslateY);
      return;
    }

    // Handle pinch zoom (two touches)
    if (isPinching && activeImg && isValidActiveImage(activeImg) && e.touches.length === 2) {
      // When zoomed, prevent lightbox swipe/scroll
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();

      const currentDistance = distance(e.touches[0], e.touches[1]);
      const scaleFactor = currentDistance / Math.max(1, startDistance);
      const nextScale = startScale * scaleFactor;
      
      // Get current translate to preserve it during zoom
      const currentTranslate = getTranslateFromElement(activeImg);
      setScaleOnElement(activeImg, nextScale, currentTranslate.x, currentTranslate.y);
      return;
    }
  }

  function handleTouchEnd(e) {
    if (!activeImg) return;

    // Validate active image
    if (!isValidActiveImage(activeImg)) {
      // Image is no longer valid, reset state
      const prevScale = getScaleFromElement(activeImg);
      if (prevScale > 1.02) {
        resetScale(activeImg);
      }
      activeImg = null;
      isPanning = false;
      isPinching = false;
      return;
    }

    if (isPanning && e.touches.length === 0) {
      // End of pan
      isPanning = false;
      // Don't reset activeImg here, allow continued panning if user touches again
      // But allow swipe after a short delay
      swipeBlockedUntil = Date.now() + 100;
    }

    if (isPinching && e.touches.length < 2) {
      // End of pinch
      isPinching = false;
      const scaleNow = getScaleFromElement(activeImg);
      if (scaleNow <= 1.02) {
        resetScale(activeImg);
        activeImg = null;
      } else {
        // Image is still zoomed, keep it active for potential panning
        // But reset panning state
        isPanning = false;
      }
      // Set cooldown period to prevent accidental swipes after pinch
      swipeBlockedUntil = Date.now() + 500; // 500ms cooldown
    }

    // If all touches are gone and we're not zoomed, reset everything
    if (e.touches.length === 0 && !isPinching && !isPanning) {
      const scaleNow = activeImg ? getScaleFromElement(activeImg) : 1;
      if (scaleNow <= 1.02) {
        if (activeImg) resetScale(activeImg);
        activeImg = null;
      }
    }

    // Prevent the parent lightbox from interpreting this as a swipe end
    if (isPanning || isPinching || (activeImg && isValidActiveImage(activeImg) && getScaleFromElement(activeImg) > 1.02)) {
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      else e.stopPropagation();
    }
  }

  // Delegate on document in capture phase to intercept before lightbox handlers
  document.addEventListener('touchstart', handleTouchStart, optsStart);
  document.addEventListener('touchmove', handleTouchMove, optsMove);
  document.addEventListener('touchend', handleTouchEnd, optsEnd);
  document.addEventListener('touchcancel', handleTouchEnd, optsEnd);

  teardown = () => {
    document.removeEventListener('touchstart', handleTouchStart, optsStart);
    document.removeEventListener('touchmove', handleTouchMove, optsMove);
    document.removeEventListener('touchend', handleTouchEnd, optsEnd);
    document.removeEventListener('touchcancel', handleTouchEnd, optsEnd);
  };
}

export default () => {
  if (process.server) return;
  if (typeof window === 'undefined') return;

  // Install once per page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onMounted, { once: true });
  } else {
    onMounted();
  }

  // Optional: expose cleanup for HMR (Nuxt 2 / Webpack)
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.dispose(function () {
      if (teardown) teardown();
    });
  }
};
